import { useEffect, useState, useTransition, useCallback } from 'react';
import { useTenant } from './context/TenantContext.jsx';
import { useCart } from './store/cart.jsx';
import { parseRoute, buildRouteUrl } from './lib/router.js';
import { ShellHeader } from './components/layout/ShellHeader.jsx';
import { ShellFooter } from './components/layout/ShellFooter.jsx';
import { HomeView } from './features/home/HomeView.jsx';
import { CatalogView } from './features/catalog/CatalogView.jsx';
import { ProductDetailView } from './features/product/ProductDetailView.jsx';
import { CartView } from './features/cart/CartView.jsx';
import { CartDrawer } from './features/cart/CartDrawer.jsx';
import { CopilotDrawer } from './components/copilot/CopilotDrawer.jsx';
import { InquirySuccessView } from './features/inquiry/InquirySuccessView.jsx';
import { CheckoutSuccessView } from './features/checkout/CheckoutSuccessView.jsx';
import { LegalInfoView } from './features/legal/LegalInfoView.jsx';
import { NotFoundView } from './features/errors/NotFoundView.jsx';
import { ContactView } from './features/legal/ContactView.jsx';
import { AdminDashboardView } from './features/admin/AdminDashboardView.jsx';
import { EditToolbar } from './components/editing/EditToolbar.jsx';
import { EditorLoginLauncher } from './components/editing/EditorLoginLauncher.jsx';
import { DesignDrawer } from './components/editing/DesignDrawer.jsx';
import { ExecutiveHubView } from './features/executive/ExecutiveHubView.jsx';
import { Skeleton } from './components/ui/Skeleton.jsx';
import { TenantLogo } from './components/tenant/TenantLogo.jsx';
import { SecurityAdvisor } from './features/advisor/SecurityAdvisor.jsx';
import { isAdvisorEntryEnabled } from './lib/advisor-gate.js';
import { showInternalTools } from './lib/internal-tools.js';

export default function App() {
  const { tenant, loading: tenantLoading, error: tenantError } = useTenant();
  const cart = useCart();
  // Berater-Gate (Paket D-UI): advisor_enabled === false blendet CTA & Route aus
  const advisorEnabled = isAdvisorEntryEnabled(tenant);
  const [route, setRoute] = useState(() => parseRoute());
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [, startTransition] = useTransition();

  // Synchronize browser history / popstate
  useEffect(() => {
    const handlePopState = () => {
      startTransition(() => {
        setRoute(parseRoute());
      });
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);

  const navigate = useCallback((targetRoute, { replace = false } = {}) => {
    let url = typeof targetRoute === 'string' ? targetRoute : buildRouteUrl(targetRoute);
    // OCI-Modus über die gesamte Sitzung erhalten (PunchOut-Vertrag: Start -> Katalog -> Warenkorb -> Übergabe)
    if (typeof window !== 'undefined'
      && new URL(window.location.href).searchParams.get('mode') === 'oci'
      && !url.includes('mode=oci')) {
      url += (url.includes('?') ? '&' : '?') + 'mode=oci';
    }
    if (replace) {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    startTransition(() => {
      setRoute(parseRoute());
    });
  }, []);

  const handleAddToCart = useCallback((product, quantity = 1) => {
    cart.addItem(product, quantity);
    setCartDrawerOpen(true);
  }, [cart]);

  // a-042: ASL-interne Werkzeuge (Hub, Copilot) nur mit internal_tools-Flag. Kundenshop: Hub leitet auf Startseite um.
  const internalToolsOn = showInternalTools(tenant?.settings);
  useEffect(() => {
    if (tenantLoading || route.name !== 'hub' || internalToolsOn) return;
    navigate('/', { replace: true });
  }, [tenantLoading, route.name, internalToolsOn, navigate]);

  if (tenantLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 text-center bg-white">
        <h1 className="text-2xl font-bold text-[var(--foreground)]">Partner-Shop nicht erreichbar</h1>
        <p className="mt-2 text-sm text-[var(--secondary)]">
          Der angeforderte Mandanten-Shop ist derzeit nicht aktiv oder nicht zugeordnet.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          data-testid="tenant-reload"
          className="mt-6 rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-bold text-white cursor-pointer hover:opacity-90"
        >
          Neu laden
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--background)] text-[var(--foreground)] selection:bg-[var(--primary)]/15 selection:text-[var(--primary)]">
      {route.name === 'advisor' && advisorEnabled ? (
        <header className="advisor-shell-header">
          <div className="advisor-shell-inner">
            <div className="advisor-shell-brand"><TenantLogo tenant={tenant} showSlogan={false} className="h-7 w-auto" /><span>Partner-Musteransicht</span></div>
            <button className="advisor-text-button" onClick={() => navigate('/')}>Zum Shop →</button>
          </div>
          <p className="advisor-shell-disclaimer">Kein offizieller ASL-Onlineshop</p>
        </header>
      ) : route.name === 'admin' ? null : <ShellHeader
        route={route}
        onNavigate={navigate}
        cartCount={cart.totalItems || 0}
        onOpenCart={() => setCartDrawerOpen(true)}
        onOpenCopilot={() => setCopilotOpen(true)}
      />}

      {advisorEnabled && route.name !== 'advisor' && (
        <div className="border-b border-[var(--border)] bg-white">
          <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-2 px-4 py-2 sm:px-6 lg:px-8">
            <p className="text-xs text-[var(--secondary)]">Musteransicht für ASL-Partner · Kein offizieller ASL-Onlineshop</p>
            <button className="min-h-11 text-sm font-semibold text-[var(--primary)] underline-offset-4 hover:underline" onClick={() => navigate(route.name === 'advisor' ? '/' : '/berater')}>
              {route.name === 'advisor' ? 'Zur Shopansicht' : 'Sicherheitsberater öffnen →'}
            </button>
          </div>
        </div>
      )}

      <main className="flex-1">
        {route.name === 'advisor' && (advisorEnabled
          ? <SecurityAdvisor onNavigate={navigate} />
          : <p className="p-8">Der Sicherheitsberater ist für diesen Shop noch nicht freigeschaltet.</p>)}
        {route.name === 'home' && (
          <HomeView
            onNavigate={navigate}
            onAddToCart={handleAddToCart}
          />
        )}

        {route.name === 'catalog' && (
          <CatalogView
            route={route}
            onNavigate={navigate}
            onAddToCart={handleAddToCart}
          />
        )}

        {route.name === 'product' && (
          <ProductDetailView
            slug={route.slug || route.params?.idOrSlug}
            idOrSlug={route.slug || route.params?.idOrSlug}
            onNavigate={navigate}
            onAddToCart={handleAddToCart}
            onOpenCopilot={(prompt) => {
              setCopilotOpen(true);
              if (prompt && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('asl:open-copilot', { detail: { query: prompt, prompt } }));
              }
            }}
          />
        )}

        {route.name === 'cart' && (
          <CartView
            onNavigate={navigate}
            onSuccess={(inquiry) => {
              const inqId = inquiry?.inquiryNumber || inquiry?.inquiry_number || inquiry?.id || '';
              navigate(`/inquiry/success/${inqId}`);
            }}
          />
        )}

        {route.name === 'checkout-success' && (
          <CheckoutSuccessView onNavigate={navigate} />
        )}

        {(route.name === 'inquiry-success' || route.name === 'success') && (
          <InquirySuccessView
            inquiryId={route.inquiryId || route.params?.inquiryId}
            onNavigate={navigate}
          />
        )}

        {route.name === 'legal' && (route.page || route.params?.page) === 'kontakt' && (
          <ContactView />
        )}

        {route.name === 'legal' && (route.page || route.params?.page) !== 'kontakt' && (
          <LegalInfoView
            page={route.page || route.params?.page}
            onNavigate={navigate}
          />
        )}

        {route.name === 'notfound' && <NotFoundView onNavigate={navigate} />}

        {route.name === 'admin' && (
          <AdminDashboardView
            section={route.params?.section}
            onNavigate={navigate}
          />
        )}

        {route.name === 'hub' && internalToolsOn && (
          <ExecutiveHubView
            route={route}
            onNavigate={navigate}
            onOpenCopilot={(prompt) => {
              setCopilotOpen(true);
              if (prompt && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('asl:open-copilot', { detail: { query: prompt, prompt } }));
              }
            }}
          />
        )}
      </main>

      <CartDrawer
        open={cartDrawerOpen}
        onOpenChange={setCartDrawerOpen}
        onNavigate={navigate}
      />

      {internalToolsOn && (
        <CopilotDrawer
          open={copilotOpen}
          onOpenChange={setCopilotOpen}
          onNavigate={navigate}
        />
      )}

      {(route.name !== 'advisor' && route.name !== 'admin') && <ShellFooter onNavigate={navigate} />}
      <EditToolbar />
      {route.name !== 'admin' && <EditorLoginLauncher />}
      <DesignDrawer />
    </div>
  );
}
